/**
 * Created by SH 16/03/2019
 */
import { Helper } from "../../utils/Helper";
import { Presentation } from "./Presentation";
import { Logger } from "@nolimitcity/slot-launcher/bin/utils/Logger";
import { GameModel } from "@nolimitcity/slot-game/bin/model/GameModel";
import { FlowStateController } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import { FlowStatePresentation } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { APIEvent } from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import { gsap } from "gsap";

export class PresentationController implements FlowStatePresentation {
    private timeline: gsap.core.Timeline;
    private dataModel: GameModel<any, any>;
    private presentations: Presentation[] = [];
    private presentation: Presentation | undefined;
    private flowStateController: FlowStateController;
    private modeState: string;

    // Getter Shortcut for game data
    private get GameData(): any {
        return this.dataModel.gameData;
    }

    // Getter Shortcut for initialization data
    private get InitData(): any {
        return this.dataModel.initData;
    }

    constructor(_presentations: Presentation[], _model: GameModel<any, any>, _modeState: string) {
        this.dataModel = _model;
        this.presentations = _presentations;
        this.modeState = _modeState;
    }

    // Initialize presentations with initial data
    public InitPresentations(): void {
        const initData = this.InitData;
        this.presentations.forEach(p => {
            if (p.setInitData && initData.mode === this.modeState) {
                p.setInitData(initData);
            }
            if (p.initialization) {
                p.initialization(this.modeState);
            }
        });
    }

    // Start the presentation controller
    public start(flowStateController: FlowStateController): void {
        this.flowStateController = flowStateController;
        this.checkAndParseData();
        this.tryPlayNextPresentation();
    }

    // Check and parse data to activate presentations
    private checkAndParseData(): void {
        const gameData = this.GameData;
        const initData = this.InitData;
        this.presentations.forEach(presentation => {
            const data = gameData ? gameData : initData;
            if (presentation.checkForActivation(data)) {
                presentation.setPlayable(true);
                if (presentation.setGameData && gameData) {
                    presentation.setGameData(gameData);
                }
            }
        });
    }

    // Try to play the next active presentation
    private tryPlayNextPresentation(): void {
        if (this.findActive()) {
            this.startPresentation();
        } else {
            this.finish();
        }
    }

    // Start the current active presentation
    private startPresentation(): void {
        requestAnimationFrame(() => {
            this.timeline = Helper.setTimeline(this.timeline, {
                paused: true,
                onComplete: () => this.recursive()
            });
            this.presentation!.timeline = this.timeline;
            this.presentation!.createPresentation((enable: boolean) => this.enableSkip(enable));
            if (this.presentation!.timeline.totalDuration()) {
                this.presentation!.timeline.play();
            } else {
                this.recursive();
            }
        });
    }

    // Enable or disable the skip functionality
    private enableSkip(enable: boolean = true): void {
        this.flowStateController.setSkipCallback(enable ? () => {
            if (this.presentation) {
                this.presentation.skip();
            } else {
                Logger.log("Tried to skip but couldn't find " + (this.presentation ? "presentation" : "skip-method."));
            }
        } : undefined);
        if (!enable) {
            SlotGame.apiPlugin.events.trigger(APIEvent.SKIP);
        }
    }

    // Recursive function to handle presentation flow
    private recursive(): void {
        if (!this.checkFinished() || this.checkIsStillActive()) {
            this.startPresentation();
        } else {
            this.triggerDone();
            this.tryPlayNextPresentation();
        }
    }

    // Find the next active presentation
    private findActive(): Presentation | undefined {
        this.presentation = this.presentations.find(p => p.isActive);
        if (this.presentation && this.presentation.preStart) {
            this.presentation.preStart();
        }
        return this.presentation;
    }

    // Check if the current presentation is finished
    private checkFinished(): boolean {
        return this.presentation!.isFinished();
    }

    // Check if the current presentation is still active
    private checkIsStillActive(): boolean {
        return this.presentation!.isActive;
    }

    // Trigger the done method of the current presentation
    private triggerDone(): void {
        if (this.presentation!.done) {
            this.presentation!.done();
        }
    }

    // Finish the flow state controller
    private finish(): void {
        this.flowStateController.finish();
    }
}